// widgets/MoodWidget.tsx
import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Smile, Frown, Meh, Sun, Cloud, AlertCircle } from "lucide-react";

const MoodWidget: React.FC = () => {
    const { t, i18n } = useTranslation("healthSensors");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const tabs = t("tabs", { returnObjects: true }) as any[];
    const moodTab = tabs.find((tab) => tab.id === "mood");
    const widget = moodTab?.widget || {};

    // Mood icon mapping
    const getMoodIcon = (moodType: string) => {
        const icons: Record<string, React.ReactNode> = {
            happy: <Smile className="w-8 h-8" />,
            neutral: <Meh className="w-8 h-8" />,
            sad: <Frown className="w-8 h-8" />,
        };
        return icons[moodType] || <Meh className="w-8 h-8" />;
    };

    const getMoodColor = (moodType: string) => {
        const colors: Record<string, string> = {
            happy: "from-green-400 to-emerald-500",
            neutral: "from-yellow-400 to-amber-500",
            sad: "from-blue-400 to-cyan-500",
        };
        return colors[moodType] || "from-gray-400 to-gray-500";
    };

    return (
        <motion.div
            className="relative w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
        >
            {/* Main widget */}
            <div className="bg-gradient-to-br from-white/90 via-amber-50/30 to-yellow-100/20 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-amber-200/40 mb-4 overflow-hidden relative">
                {/* Background effects */}
                <div className={`absolute ${isRTL ? "left-10" : "right-10"} -top-10 w-40 h-40 bg-gradient-to-br from-amber-200 to-yellow-100 rounded-full opacity-20 blur-2xl`}></div>

                {/* Header */}
                <div className="flex justify-between items-center mb-6 relative z-10">
                    <div className="flex items-center">
                        <motion.div
                            className={`me-3 p-3 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-2xl shadow-lg`}
                            animate={{
                                rotate: [0, 5, -5, 0],
                                scale: [1, 1.05, 1],
                            }}
                            transition={{
                                duration: 3,
                                repeat: Infinity,
                            }}
                        >
                            <Smile className="w-6 h-6 text-white" />
                        </motion.div>
                        <div>
                            <h3 className="text-xl font-bold text-amber-900 mb-1">{widget.title}</h3>
                            <p className="text-sm text-amber-600">{widget.subtitle}</p>
                        </div>
                    </div>
                </div>

                {/* Current mood display */}
                <div className="relative mb-6">
                    <motion.div
                        className="flex flex-col items-center justify-center p-8 bg-white/60 backdrop-blur-md rounded-3xl border border-amber-200/50 shadow-lg"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                    >
                        <motion.div
                            className={`w-24 h-24 rounded-full bg-gradient-to-br ${getMoodColor(widget.currentMood?.type)} flex items-center justify-center text-white shadow-2xl mb-4`}
                            animate={{
                                scale: [1, 1.1, 1],
                                rotate: [0, 5, -5, 0],
                            }}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                            }}
                        >
                            {getMoodIcon(widget.currentMood?.type)}
                        </motion.div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">{widget.currentMood?.label}</h3>
                        <p className="text-sm text-gray-600">{widget.currentMood?.description}</p>
                    </motion.div>
                </div>

                {/* Mood timeline */}
                <div className="space-y-3 mb-6">
                    <h4 className="text-sm font-semibold text-amber-900 mb-3">{widget.timeline?.title}</h4>
                    <div className="flex items-center justify-between gap-2">
                        {widget.timeline?.items?.map((item: any, index: number) => (
                            <motion.div
                                key={index}
                                className="flex-1 flex flex-col items-center"
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.5 + index * 0.1 }}
                            >
                                <motion.div
                                    className={`w-12 h-12 rounded-full bg-gradient-to-br ${getMoodColor(item.type)} flex items-center justify-center text-white shadow-md mb-2`}
                                    whileHover={{ scale: 1.1, y: -3 }}
                                >
                                    {getMoodIcon(item.type)}
                                </motion.div>
                                <span className="text-xs text-gray-600">{item.time}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Mood factors */}
                <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-amber-900 mb-3">{widget.factors?.title}</h4>
                    {widget.factors?.items?.map((factor: any, index: number) => (
                        <motion.div
                            key={index}
                            className="flex items-center justify-between p-3 bg-white/50 backdrop-blur-sm rounded-xl"
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.8 + index * 0.1 }}
                        >
                            <div className="flex items-center gap-2">
                                <span className="text-2xl">{factor.icon}</span>
                                <span className="text-sm font-medium text-gray-800">{factor.label}</span>
                            </div>
                            <span className={`text-sm font-bold ${factor.impact === "positive" ? "text-green-600" : factor.impact === "negative" ? "text-red-600" : "text-gray-600"}`}>
                                {factor.value}
                            </span>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Guardian alerts */}
            {widget.alerts && widget.alerts.length > 0 && (
                <motion.div
                    className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-4 border border-red-200/50 shadow-lg"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                >
                    <div className="flex items-center mb-3">
                        <AlertCircle className={`w-5 h-5 text-red-600 me-2`} />
                        <h4 className="font-bold text-red-900">{widget.alertsTitle}</h4>
                    </div>
                    <div className="space-y-2">
                        {widget.alerts.map((alert: string, index: number) => (
                            <motion.p
                                key={index}
                                className="text-sm text-red-700 flex items-start gap-2"
                                initial={{ x: -10, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.8 + index * 0.1 }}
                            >
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0"></span>
                                {alert}
                            </motion.p>
                        ))}
                    </div>
                </motion.div>
            )}
        </motion.div>
    );
};

export default MoodWidget;
